import { getLoadedBoard, LevelConfig, MapList, NODES_PER_MAP } from '../../Config/Levels';
import BoardUtils from '../../Game/BoardUtils';
import { BoosterTilePostfix, Suits } from '../../Tiles/TileSet';
import * as fromActions from '../Actions/Actions';
import { ActionTypes } from '../Actions/Actions';
import { ItemIDs } from '../../Config/Items';

export interface IStateLevel {
    prelevel: string;
    levelId: string | null;
    levelIndex: number;
    matchNum: number;
    matches: string[];
    selected: string[];
    goal: ILevelGoal;
    found: string[];
    counted: string[];
    complete: boolean;
    stuck: boolean;
    stuckReason: StuckReasons;
    shuffling: boolean;
    removes: string[];
    hints: string[];
    winTime: number;
    timed: boolean;
    attempts: number;
}

export interface ILevelGoal {
    type: string;
    total: number;
    target: number;
}

export const defaultStateLevel: IStateLevel = {
    complete: false,
    counted: [],
    found: [],
    goal: { type: 'pair', total: 0, target: 0 },
    hints: [],
    levelId: null,
    levelIndex: 0,
    matchNum: 2,
    matches: [],
    prelevel: '',
    removes: [],
    selected: [],
    shuffling: false,
    stuck: false,
    stuckReason: null,
    winTime: 0,
    timed: false,
    attempts: 0,
};

export enum StuckReasons {
    MATCHES,
    TIME,
}

export interface IStuckArgs {
    reason?: StuckReasons;
    stuck?: boolean;
}

export const StateLevel = (state = defaultStateLevel, action: fromActions.Actions): IStateLevel => {
    switch (action.type) {
        case ActionTypes.ADD_GAME_BOOSTER:
            const s =
                action.payload[0].indexOf(ItemIDs.MAGNET) !== -1 ||
                action.payload[0].indexOf(ItemIDs.FIRECRACKER) !== -1
                    ? []
                    : state.selected.slice(0);
            return {
                ...state,
                selected: s,
            };
        case ActionTypes.CLEAR_PRELEVEL:
            return {
                ...state,
                prelevel: '',
            };
        case ActionTypes.COUNT_OBJECTS:
            const newFound = action.payload.filter(f => state.counted.indexOf(f) === -1);
            const counted = [...state.counted, ...newFound];
            return {
                ...state,
                counted,
                found: [],
                goal: {
                    ...state.goal,
                    total: state.goal.total + newFound.length,
                },
            };
        case ActionTypes.FIND_OBJECT:
            return {
                ...state,
                found: [...state.found, ...action.payload],
            };
        case ActionTypes.INIT_LEVEL:
            return {
                ...state,
                prelevel: action.payload,
            };
        case ActionTypes.LEVEL_COMPLETE:
            return {
                ...state,
                stuck: false,
                complete: true,
            };
        case ActionTypes.LEVEL_STUCK:
            const stuck = state.complete ? false : action.payload.stuck;
            return {
                ...state,
                stuck,
                attempts:
                    action.payload.stuck && !state.stuck ? state.attempts + 1 : state.attempts,
                stuckReason: action.payload.reason,
            };
        case ActionTypes.MATCH_TILES:
            const m = action.payload.length === state.matchNum ? action.payload : [];
            const ms = m.length > 0 ? [] : [state.selected.pop()];
            return {
                ...state,
                matches: m,
                selected: ms,
            };
        case ActionTypes.REGISTER_FOUND:
            // will need adjustment here if we have more than one collectable item type on the board
            // const registered = state.goal.type !== 'pair' ? action.payload : [];
            return {
                ...state,
                counted: [],
            };
        case ActionTypes.REGISTER_MOVE:
            const counts =
                state.goal.type === 'pair' || state.goal.type === 'gold'
                    ? action.payload.filter(f => f === state.goal.type)
                    : [];
            return {
                ...state,
                goal: {
                    ...state.goal,
                    total: state.goal.total + counts.length,
                },
            };
        case ActionTypes.REMOVE_GAME_BOOSTER:
            return {
                ...state,
                // paused: false,
                selected:
                    action.payload[0] &&
                    (action.payload[0] === ItemIDs.MAGNET ||
                        action.payload[0].includes(BoosterTilePostfix))
                        ? []
                        : state.selected.slice(0),
                hints: action.payload[0] === ItemIDs.HINT ? [] : state.hints.slice(0),
            };
        case ActionTypes.REMOVE_MATCH:
            return {
                ...state,
                matches: [],
            };
        case ActionTypes.REMOVE_STORED:
            const r = state.removes.slice(0).filter(f => f !== action.payload);
            return {
                ...state,
                removes: r,
            };
        case ActionTypes.SELECT_GAME_LEVEL:
            return {
                ...state,
                prelevel: action.payload,
            };
        case ActionTypes.SELECT_TILE:
            const newSelected = addToSelected(
                state.selected,
                state.matchNum,
                action.payload,
                state.matches
            );
            return {
                ...state,
                selected: newSelected,
            };
        case ActionTypes.SET_TILESPRITES:
            return {
                ...state,
                shuffling: false,
            };
        case ActionTypes.SHUFFLE_TILES:
            return {
                ...state,
                shuffling: true,
                hints: [],
            };
        case ActionTypes.CHANGE_MAP:
            return {
                ...state,
                levelIndex: 0,
            };
        case ActionTypes.START_LEVEL:
            const init = initLevel(action.payload);
            const index = getLevelIndex(action.payload);
            return {
                ...defaultStateLevel,
                ...init,
                complete: false,
                levelIndex: index,
                matches: [],
                prelevel: '',
                selected: [],
                stuck: false,
            };
        case ActionTypes.STORE_HINTS:
            const hints = [...state.hints, ...action.payload];
            return {
                ...state,
                hints,
            };
        case ActionTypes.STORE_MATCHES:
            const stored = [...state.removes, ...action.payload];
            return {
                ...state,
                removes: stored,
            };
        default:
            return state;
    }
};

const initLevel = (id: string) => {
    const loaded = getLoadedBoard();
    const boosterTiles = loaded.tiles.filter(
        tile => BoardUtils.convertTileData(tile.type).suit === Suits.BOOSTER
    );
    const numTiles = loaded.tiles.length + boosterTiles.length;
    const levelTarget =
        typeof loaded.config.goal.target === 'number'
            ? loaded.config.goal.target
            : Math.floor(numTiles / 2);
    return {
        ...defaultStateLevel,
        levelId: id,
        goal: { ...loaded.config.goal, target: levelTarget, total: 0 },
        winTime: loaded.config.winTime,
        timed: loaded.config.winTime > 0,
    };
};

const addToSelected = (selected: string[], targetLen: number, id: string, matches: string[]) => {
    selected = selected.splice(0);
    if (!selected.includes(id) && selected.length < targetLen && !matches.includes(id)) {
        selected.push(id);
    }
    return selected;
};

export const getLevelIndex = (id: string): number => {
    const mapId = id.substr(0, id.indexOf('-'));
    const mapIndex = Object.keys(LevelConfig).indexOf(mapId);
    const before = Object.keys(LevelConfig)
        .slice(0, mapIndex)
        .reduce((acc, mKey) => {
            const m = LevelConfig[mKey];
            acc += Object.keys(m).length;
            return acc;
        }, 0);
    const levelIndex = parseInt(id.substr(id.indexOf('-') + 1), 10);
    return before + levelIndex;
};

export const getLevelId = (index: number): string => {
    const mapIndex = Math.floor(index / NODES_PER_MAP);
    const mapId = MapList[mapIndex];
    const levelIndex = index - mapIndex * NODES_PER_MAP;
    return mapId + '-' + levelIndex.toString(10);
};

export const getMatch = (vals: string[]): boolean => {
    const suit = vals[0].substr(0, vals[0].indexOf('-'));
    if (suit === Suits.FLOWERS || suit === Suits.SEASONS) {
        vals = vals.map(v => v.substr(0, v.indexOf('-')));
    }
    return vals.filter(f => f === vals[0]).length === vals.length;
};
